import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { toast } from 'react-toastify'
import { FiList, FiAlertCircle, FiSearch, FiDownload, FiEdit, FiX } from 'react-icons/fi'
import { MaterialReactTable } from 'material-react-table'
import * as XLSX from 'xlsx'
import { Modal, Input, Button, Tag, Space, Select, Checkbox, Tooltip } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { 
  getNotificacionesCambios, 
  getRepuestosPorCodigoSap,
  putUpdateRevision,
  putRelacionReemplazante,
  putDesvincularReemplazante,
  putRelacionHomologo,
  putDesvincularHomologo,
  putRelacionModelo,
  putDesvincularModelo,
  getAllRepuestos,
  getReferenciasRepuesto,
  getIam,
  getMarcas,
  getCategorias,
  getSubCategorias,
  getSegmentos,
  getSubSegmentos,
  getModelosAplicacion,
  getPrioridades,
  postNotificarCambioResponsable,
  putNotificacionEnProceso,
  putRevisionActualizacionCatalogoCorregido,
} from '../../services/axiomaService'
import { useJerarquiaCascada, HABILITAR_JERARQUIA_CASCADA } from '../../components/Axioma/hooks/useJerarquiaCascada'
import './AxiomaValidadoresContainer.css'

/** Normaliza la respuesta de /v1/modelos_aplicacion a un arreglo. */
const extraerModelosAplicacion = (data) => {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.modelos_aplicacion)) return data.modelos_aplicacion
  if (Array.isArray(data.modelos)) return data.modelos
  if (Array.isArray(data.success)) return data.success
  if (Array.isArray(data.success?.modelos_aplicacion)) {
    return data.success.modelos_aplicacion
  }
  if (Array.isArray(data.success?.modelos)) return data.success.modelos
  if (Array.isArray(data.data)) return data.data
  return []
}

const AxiomaValidadoresContainer = () => {
  const [notificaciones, setNotificaciones] = useState([])
  const [loading, setLoading] = useState(false)
  const [filtroEstado, setFiltroEstado] = useState('') 
  const [filtroUrgente, setFiltroUrgente] = useState('') // '', '0', '1'
  const [filtroVariable, setFiltroVariable] = useState('') 
  const [filtroCodigoMaterial, setFiltroCodigoMaterial] = useState('')
  const [filtroVertical, setFiltroVertical] = useState('')
  const [prioridadTodas, setPrioridadTodas] = useState(true) // true = no filtrar por prioridad (envía null)
  const [filtroPrioridad, setFiltroPrioridad] = useState([])
  const [prioridades, setPrioridades] = useState([])
  
  // Estados para el modal de edición
  const [modalVisible, setModalVisible] = useState(false)
  const [notificacionSeleccionada, setNotificacionSeleccionada] = useState(null)
  const [materialData, setMaterialData] = useState(null)
  const [loadingMaterial, setLoadingMaterial] = useState(false)
  const [descripcionCorrecta, setDescripcionCorrecta] = useState('')
  const [reemplazantes, setReemplazantes] = useState([])
  const [sustitutos, setSustitutos] = useState([])
  const [modelosAplicacion, setModelosAplicacion] = useState([])
  const [modelosAplicacionPrincipal, setModelosAplicacionPrincipal] = useState([])
  const [repuestosDisponibles, setRepuestosDisponibles] = useState([])
  const [buscandoRepuesto, setBuscandoRepuesto] = useState(false)
  const [codigoBusqueda, setCodigoBusqueda] = useState('')
  
  // Estados para JERARQUIA y MARCA
  const [marcas, setMarcas] = useState([])
  const [categorias, setCategorias] = useState([])
  const [subcategorias, setSubcategorias] = useState([])
  const [segmentos, setSegmentos] = useState([])
  const [subsegmentos, setSubsegmentos] = useState([])

  // Cascada de jerarquía por sector del material (si el material trae sector,
  // los selects de JERARQUIA se filtran nivel a nivel en lugar de usar las
  // listas completas de arriba)
  const {
    activa: cascadaActiva,
    categorias: categoriasCascada,
    subcategorias: subcategoriasCascada,
    segmentos: segmentosCascada,
    subsegmentos: subsegmentosCascada,
    iniciar: iniciarCascada,
    cambiarCategoria: cambiarCategoriaCascada,
    cambiarSubcategoria: cambiarSubcategoriaCascada,
    cambiarSegmento: cambiarSegmentoCascada,
  } = useJerarquiaCascada()
  const [marcaSeleccionada, setMarcaSeleccionada] = useState(null)
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null)
  const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState(null)
  const [segmentoSeleccionado, setSegmentoSeleccionado] = useState(null)
  const [subsegmentoSeleccionado, setSubsegmentoSeleccionado] = useState(null)
  
  // Estados para modelos de aplicación
  const [modelosDisponibles, setModelosDisponibles] = useState([])

  useEffect(() => {
    cargarPrioridades()
  }, [])

  useEffect(() => {
    cargarNotificaciones()
  }, [])

  const cargarPrioridades = async () => {
    try {
      const response = await getPrioridades()
      if (response && Array.isArray(response)) {
        setPrioridades(response)
      } else if (response && response.success && Array.isArray(response.success)) {
        setPrioridades(response.success)
      }
    } catch (error) {
      console.error('Error al cargar prioridades:', error)
    }
  }

  const cargarNotificaciones = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filtroEstado) params.estado = filtroEstado
      if (filtroUrgente) params.urgente = filtroUrgente
      if (filtroVariable) params.variable = filtroVariable
      if (filtroCodigoMaterial) params.codigoMaterial = filtroCodigoMaterial
      if (filtroVertical) params.vertical = filtroVertical
      // Solo enviar prioridad si "Todas" no está activo; si está activo no se envía (null, no filtra)
      if (!prioridadTodas && Array.isArray(filtroPrioridad) && filtroPrioridad.length > 0) {
        params.prioridad = filtroPrioridad.map((p) => String(p))
      }

      const response = await getNotificacionesCambios(params)
      
      if (response && response.notificaciones) {
        setNotificaciones(response.notificaciones)
        console.log('Notificaciones cargadas:', response.notificaciones)
      } else {
        toast.error('Error al cargar las notificaciones')
      }
    } catch (error) {
      console.error('Error al cargar notificaciones:', error)
      toast.error('Error al cargar las notificaciones')
    } finally {
      setLoading(false)
    }
  }

  const limpiarFiltros = () => {
    setFiltroEstado('')
    setFiltroUrgente('')
    setFiltroVariable('')
    setFiltroCodigoMaterial('')
    setFiltroVertical('')
    setPrioridadTodas(true)
    setFiltroPrioridad([])
  }

  const handleEnProcesoChange = useCallback(async (notificacionId, checked) => {
    const valor = checked ? 1 : 0
    try {
      await putNotificacionEnProceso(notificacionId, valor)
      setNotificaciones((prev) =>
        prev.map((n) =>
          n.id_notificacion === notificacionId ? { ...n, en_proceso: valor } : n
        )
      )
      toast.success(valor ? 'Marcado como en proceso' : 'Desmarcado como en proceso')
    } catch (error) {
      toast.error('Error al actualizar el estado en proceso')
    }
  }, [])

  const handleCatalogoCorregidoChange = useCallback(
    async (notificacionId, revisionId, checked) => {
      if (revisionId == null || revisionId === '') {
        toast.error('Esta notificación no tiene revisión asociada.')
        return
      }
      const valor = checked ? 1 : 0
      try {
        await putRevisionActualizacionCatalogoCorregido(revisionId, valor)
        setNotificaciones((prev) =>
          prev.map((n) =>
            n.id_notificacion === notificacionId
              ? { ...n, actualizacion_catalogo_corregido: valor }
              : n
          )
        )
        toast.success(
          valor ? 'Marcado como catálogo corregido' : 'Desmarcado catálogo corregido'
        )
      } catch (error) {
        toast.error('Error al actualizar catálogo corregido')
      }
    },
    []
  )

  // Función para cargar datos de selects
  const cargarDatosSelects = async () => {
    try {
      const [marcasData, categoriasData, subcategoriasData, segmentosData, subsegmentosData, modelosData] = await Promise.all([
        getMarcas(),
        getCategorias(),
        getSubCategorias(),
        getSegmentos(),
        getSubSegmentos(),
        getModelosAplicacion()
      ])
      
      if (marcasData?.marcas) setMarcas(marcasData.marcas)
      if (categoriasData?.categorias) setCategorias(categoriasData.categorias)
      if (subcategoriasData?.subcategorias) setSubcategorias(subcategoriasData.subcategorias)
      if (segmentosData?.segmentos) setSegmentos(segmentosData.segmentos)
      if (subsegmentosData?.subsegmentos) setSubsegmentos(subsegmentosData.subsegmentos)
      const listaModelos = extraerModelosAplicacion(modelosData)
      if (listaModelos.length > 0) setModelosDisponibles(listaModelos)
    } catch (error) {
      console.error('Error al cargar datos de selects:', error)
    }
  }

  // Función para abrir el modal de edición
  const handleAbrirModal = async (notificacion) => {
    setNotificacionSeleccionada(notificacion)
    setModalVisible(true)
    setLoadingMaterial(true)
    
    try {
      // Listas completas para MARCA; para JERARQUIA solo si la cascada por sector está desactivada
      if (
        notificacion.tipo_cambio === 'MARCA' ||
        (notificacion.tipo_cambio === 'JERARQUIA' && !HABILITAR_JERARQUIA_CASCADA)
      ) {
        await cargarDatosSelects()
      }
      
      // Cargar modelos si es necesario
      if (['MODELOS_APLICACION_PRINCIPAL', 'MODELOS_APLICACION'].includes(notificacion.tipo_cambio)) {
        const modelosData = await getModelosAplicacion()
        setModelosDisponibles(extraerModelosAplicacion(modelosData))
      }
      
      // Obtener el material por código SAP
      const response = await getRepuestosPorCodigoSap(notificacion.codigo_material_sap)
      
      // La respuesta puede venir como response.repuestos o response.success.repuestos
      const repuestos = response?.repuestos || (response?.success?.repuestos) || (Array.isArray(response?.success) ? response.success : [])
      
      if (repuestos && repuestos.length > 0) {
        const material = repuestos[0]
        setMaterialData(material)
        
        // Inicializar campos según el tipo de cambio
        if (notificacion.tipo_cambio === 'DESCRIPCION') {
          setDescripcionCorrecta(material.revision_descripcion_correcta || material.repuesto_descripcion || '')
        } else if (notificacion.tipo_cambio === 'REEMPLAZANTES' || notificacion.tipo_cambio === 'SUSTITUTOS') {
          // Cargar relaciones existentes
          if (material.revision_id) {
            const relacionesResponse = await getReferenciasRepuesto(material.revision_id)
            
            if (relacionesResponse?.relaciones) {
              if (notificacion.tipo_cambio === 'REEMPLAZANTES') {
                const reemplazantesData = relacionesResponse.relaciones.reemplazantes_sugeridos || []
                setReemplazantes(reemplazantesData)
              } else if (notificacion.tipo_cambio === 'SUSTITUTOS') {
                const sustitutosData = relacionesResponse.relaciones.homologos_sugeridos || []
                setSustitutos(sustitutosData)
              }
            }
          }
        } else if (notificacion.tipo_cambio === 'MARCA') {
          // Inicializar marca seleccionada
          setMarcaSeleccionada(material.revision_marca_sugerida_id || material.marca_id || null)
        } else if (notificacion.tipo_cambio === 'JERARQUIA') {
          // Cascada por sector (feature flag) o listas libres como antes
          if (HABILITAR_JERARQUIA_CASCADA) {
            const resuelto = await iniciarCascada(material)
            if (resuelto) {
              setCategoriaSeleccionada(resuelto.categoriaId)
              setSubcategoriaSeleccionada(resuelto.subcategoriaId)
              setSegmentoSeleccionado(resuelto.segmentoId)
              setSubsegmentoSeleccionado(resuelto.subsegmentoId)
            } else {
              await cargarDatosSelects()
              setCategoriaSeleccionada(material.revision_categoria_sugerida_id || material.categoria_id || null)
              setSubcategoriaSeleccionada(material.revision_subcategoria_sugerida_id || material.subcategoria_id || null)
              setSegmentoSeleccionado(material.revision_segmento_sugerido_id || material.segmento_id || null)
              setSubsegmentoSeleccionado(material.revision_subsegmento_sugerido_id || material.subsegmento_id || null)
            }
          } else {
            setCategoriaSeleccionada(material.revision_categoria_sugerida_id || material.categoria_id || null)
            setSubcategoriaSeleccionada(material.revision_subcategoria_sugerida_id || material.subcategoria_id || null)
            setSegmentoSeleccionado(material.revision_segmento_sugerido_id || material.segmento_id || null)
            setSubsegmentoSeleccionado(material.revision_subsegmento_sugerido_id || material.subsegmento_id || null)
          }
          setMarcaSeleccionada(material.revision_marca_sugerida_id || material.marca_id || null)
        } else if (notificacion.tipo_cambio === 'MODELOS_APLICACION' || notificacion.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL') {
          // Cargar modelos de aplicación existentes
          if (material.revision_id) {
            const relacionesResponse = await getReferenciasRepuesto(material.revision_id)
            
            if (relacionesResponse?.relaciones) {
              if (notificacion.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL') {
                const modelosData = relacionesResponse.relaciones.modelos_aplicacion_ppal_sugeridos || []
                setModelosAplicacionPrincipal(modelosData)
              } else if (notificacion.tipo_cambio === 'MODELOS_APLICACION') {
                const modelosData = relacionesResponse.relaciones.modelos_aplicacion_sugeridos || []
                setModelosAplicacion(modelosData)
              }
            }
          }
        }
      } else {
        toast.error('No se encontró el material')
        setModalVisible(false)
      }
    } catch (error) {
      console.error('Error al cargar material:', error)
      toast.error('Error al cargar el material')
      setModalVisible(false)
    } finally {
      setLoadingMaterial(false)
    }
  }

  // Función para guardar cambios
  const handleGuardarCambios = async () => {
    if (!materialData || !notificacionSeleccionada) return
    
    try {
      // Obtener usuario actual
      const userInfo = await getIam()
      const usuarioId = userInfo?.id || null
      
      // Construir el objeto base con todos los valores actuales del material
      const dataBase = {
        revision_id: materialData.revision_id || null,
        repuesto_id: materialData.repuesto_id || null,
        usuario_id: usuarioId,
        // Mantener todos los valores existentes
        estado_revision: materialData.estado,
        descripcion_breve: materialData.revision_descripcion_breve || null,
        descripcion_correcta: materialData.revision_descripcion_correcta || null,
        observacion: materialData.revision_observacion || null,
        cambio_etiqueta: materialData.revision_cambio_etiqueta || null,
        cambio_empaque: materialData.revision_cambio_empaque || null,
        cambio_descripcion: materialData.revision_cambio_descripcion || null,
        comentario_auditor: materialData.revision_comentario_auditor || null,
        estado_ax: materialData.revision_estado_ax || null,
        largo_mm: materialData.revision_largo_mm || null,
        ancho_mm: materialData.revision_ancho_mm || null,
        alto_mm: materialData.revision_alto_mm || null,
        diametro_mm: materialData.revision_diametro_mm || null,
        peso_g: materialData.revision_peso_g || null,
        unidad_empaque_recomendada: materialData.revision_unidad_empaque_recomendada || null,
        uso_aplicacion: materialData.revision_uso_aplicacion || null,
        composicion: materialData.revision_composicion || null,
        especificaciones_producto: materialData.revision_especificaciones_producto || null,
        especificaciones_electricas: materialData.revision_especificaciones_electricas || null,
        tipo_empaque_sugerido_id: materialData.revision_tipo_empaque_sugerido_id || null,
        embalaje_sugerido_id: materialData.revision_embalaje_sugerido_id || null,
        marca_sugerida_id: materialData.revision_marca_sugerida_id || null,
        categoria_sugerida_id: materialData.revision_categoria_sugerida_id || null,
        subcategoria_sugerida_id: materialData.revision_subcategoria_sugerida_id || null,
        segmento_sugerido_id: materialData.revision_segmento_sugerido_id || null,
        subsegmento_sugerido_id: materialData.revision_subsegmento_sugerido_id || null,
        descripcion_breve_ia: materialData.revision_descripcion_breve_ia || '',
        descripcion_breve_ia_modificada: materialData.revision_descripcion_breve_ia_modificada || 0,
        descripcion_correcta_urgente: materialData.revision_descripcion_correcta_urgente || 0,
      }
      
      const dataToSend = { ...dataBase }
      
      if (notificacionSeleccionada.tipo_cambio === 'DESCRIPCION') {
        // Solo actualizar descripción correcta
        dataToSend.descripcion_correcta = descripcionCorrecta
        dataToSend.cambio_descripcion = materialData.repuesto_descripcion !== descripcionCorrecta
      } else if (notificacionSeleccionada.tipo_cambio === 'MARCA') {
        // Solo actualizar marca
        dataToSend.marca_sugerida_id = marcaSeleccionada
      } else if (notificacionSeleccionada.tipo_cambio === 'JERARQUIA') {
        // Solo actualizar campos de jerarquía
        dataToSend.categoria_sugerida_id = categoriaSeleccionada
        dataToSend.subcategoria_sugerida_id = subcategoriaSeleccionada
        dataToSend.segmento_sugerido_id = segmentoSeleccionado
        dataToSend.subsegmento_sugerido_id = subsegmentoSeleccionado
        dataToSend.marca_sugerida_id = marcaSeleccionada
      }
      
      // Solo enviar si es uno de los tipos que requiere actualización de revisión
      if (['DESCRIPCION', 'MARCA', 'JERARQUIA'].includes(notificacionSeleccionada.tipo_cambio)) {
        const huTextoContext =
          notificacionSeleccionada.tipo_cambio === 'DESCRIPCION'
            ? {
                codigo: materialData.repuesto_codigo_material_sap,
                descripcionNueva: descripcionCorrecta,
                descripcionAnterior: materialData.revision_descripcion_correcta,
                estado: materialData.estado,
              }
            : undefined
        const response = await putUpdateRevision(dataToSend, huTextoContext)
        
        if (response && response.success) {
          // Obtener valores anterior y actual según el tipo de cambio
          let valorAnterior = ''
          let valorActual = ''
          
          if (notificacionSeleccionada.tipo_cambio === 'DESCRIPCION') {
            valorAnterior = materialData.revision_descripcion_correcta || materialData.repuesto_descripcion || ''
            valorActual = descripcionCorrecta || ''
          } else if (notificacionSeleccionada.tipo_cambio === 'MARCA') {
            const marcaAnterior = marcas.find(m => m.id === (materialData.revision_marca_sugerida_id || materialData.marca_id))
            const marcaActual = marcas.find(m => m.id === marcaSeleccionada)
            valorAnterior = marcaAnterior?.nombre || ''
            valorActual = marcaActual?.nombre || ''
          } else if (notificacionSeleccionada.tipo_cambio === 'JERARQUIA') {
            // Para jerarquía, construir un objeto con todos los valores
            const jerarquiaAnterior = {
              categoria: categorias.find(c => c.id === (materialData.revision_categoria_sugerida_id || materialData.categoria_id))?.nombre || '',
              subcategoria: subcategorias.find(sc => sc.id === (materialData.revision_subcategoria_sugerida_id || materialData.subcategoria_id))?.nombre || '',
              segmento: segmentos.find(s => s.id === (materialData.revision_segmento_sugerido_id || materialData.segmento_id))?.nombre || '',
              subsegmento: subsegmentos.find(ss => ss.id === (materialData.revision_subsegmento_sugerido_id || materialData.subsegmento_id))?.nombre || '',
              marca: marcas.find(m => m.id === (materialData.revision_marca_sugerida_id || materialData.marca_id))?.nombre || ''
            }
            const jerarquiaActual = {
              categoria: categorias.find(c => c.id === categoriaSeleccionada)?.nombre || '',
              subcategoria: subcategorias.find(sc => sc.id === subcategoriaSeleccionada)?.nombre || '',
              segmento: segmentos.find(s => s.id === segmentoSeleccionado)?.nombre || '',
              subsegmento: subsegmentos.find(ss => ss.id === subsegmentoSeleccionado)?.nombre || '',
              marca: marcas.find(m => m.id === marcaSeleccionada)?.nombre || ''
            }
            // Formatear jerarquía como string legible con separadores ">"
            const partesAnterior = [
              jerarquiaAnterior.categoria,
              jerarquiaAnterior.subcategoria,
              jerarquiaAnterior.segmento,
              jerarquiaAnterior.subsegmento,
              jerarquiaAnterior.marca
            ].filter(parte => parte && parte.trim() !== '')
            
            const partesActual = [
              jerarquiaActual.categoria,
              jerarquiaActual.subcategoria,
              jerarquiaActual.segmento,
              jerarquiaActual.subsegmento,
              jerarquiaActual.marca
            ].filter(parte => parte && parte.trim() !== '')
            
            valorAnterior = partesAnterior.join(' > ')
            valorActual = partesActual.join(' > ')
          }
          
          // Llamar al endpoint de notificación de cambios
          try {
            await postNotificarCambioResponsable({
              validadorId: usuarioId,
              revisionId: materialData.revision_id,
              tipoCambio: notificacionSeleccionada.tipo_cambio,
              valorAnterior,
              valorActual,
            })
          } catch (error) {
            console.error('Error al notificar cambio al responsable:', error)
            // No mostramos error al usuario ya que el cambio principal fue exitoso
          }
          
          const mensaje = 
            notificacionSeleccionada.tipo_cambio === 'DESCRIPCION' ? 'Descripción actualizada correctamente' :
            notificacionSeleccionada.tipo_cambio === 'MARCA' ? 'Marca actualizada correctamente' :
            'Jerarquía actualizada correctamente'
          
          toast.success(mensaje)
          handleCerrarModal()
          cargarNotificaciones()
        } else {
          toast.error(`Error al actualizar ${notificacionSeleccionada.tipo_cambio.toLowerCase()}`)
        }
      } else if (notificacionSeleccionada.tipo_cambio === 'REEMPLAZANTES') {
        // Los reemplazantes ya se guardan cuando se agregan/eliminan
        toast.success('Reemplazantes actualizados correctamente')
        handleCerrarModal()
        cargarNotificaciones()
      } else if (notificacionSeleccionada.tipo_cambio === 'SUSTITUTOS') {
        // Los sustitutos ya se guardan cuando se agregan/eliminan
        toast.success('Sustitutos actualizados correctamente')
        handleCerrarModal()
        cargarNotificaciones()
      } else if (notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION' || notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL') {
        // Los modelos ya se guardan cuando se agregan/eliminan
        toast.success('Modelos de aplicación actualizados correctamente')
        handleCerrarModal()
        cargarNotificaciones()
      }
    } catch (error) {
      console.error('Error al guardar cambios:', error)
      toast.error('Error al guardar los cambios')
    }
  }

  // Función para buscar repuesto por código
  const handleBuscarRepuesto = async () => {
    if (!codigoBusqueda.trim()) {
      toast.warning('Ingrese un código de material')
      return
    }
    
    setBuscandoRepuesto(true)
    try {
      const response = await getRepuestosPorCodigoSap(codigoBusqueda.trim())
      
      // La respuesta puede venir como response.repuestos o response.success.repuestos
      const repuestos = response?.repuestos || (response?.success?.repuestos) || (Array.isArray(response?.success) ? response.success : [])
      
      if (repuestos && repuestos.length > 0) {
        const repuesto = repuestos[0]
        setRepuestosDisponibles([repuesto])
        setCodigoBusqueda('')
      } else {
        toast.warning('No se encontró el material')
        setRepuestosDisponibles([])
      }
    } catch (error) {
      console.error('Error al buscar repuesto:', error)
      toast.error('Error al buscar el repuesto')
    } finally {
      setBuscandoRepuesto(false)
    }
  }

  // Función para agregar reemplazante
  const handleAgregarReemplazante = async (repuesto) => {
    if (!materialData) return
    
    try {
      const response = await putRelacionReemplazante({
        revisionId: materialData.revision_id,
        reemplazanteId: repuesto.repuesto_id,
        origen: 'AXIOMER',
      })
      
      if (response && response.success) {
        const nuevosReemplazantes = [...reemplazantes, {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          descripcion: repuesto.repuesto_descripcion
        }]
        setReemplazantes(nuevosReemplazantes)
        setRepuestosDisponibles([])
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const valorAnterior = reemplazantes.map(r => 
            `${r.codigo_material_sap || r.repuesto_codigo_material_sap} - ${r.descripcion || r.repuesto_descripcion}`
          ).join(', ') || ''
          
          const valorActual = nuevosReemplazantes.map(r => 
            `${r.codigo_material_sap || r.repuesto_codigo_material_sap} - ${r.descripcion || r.repuesto_descripcion}`
          ).join(', ')
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio: 'REEMPLAZANTES',
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Reemplazante agregado correctamente')
      } else {
        toast.error('Error al agregar el reemplazante')
      }
    } catch (error) {
      console.error('Error al agregar reemplazante:', error)
      toast.error('Error al agregar el reemplazante')
    }
  }

  // Función para eliminar reemplazante
  const handleEliminarReemplazante = async (repuestoId) => {
    if (!materialData) return
    
    try {
      const response = await putDesvincularReemplazante({
        revisionId: materialData.revision_id,
        reemplazanteId: repuestoId,
      })
      
      if (response && response.success) {
        const valorAnterior = reemplazantes.map(r => 
          `${r.codigo_material_sap || r.repuesto_codigo_material_sap} - ${r.descripcion || r.repuesto_descripcion}`
        ).join(', ') || ''
        
        const nuevosReemplazantes = reemplazantes.filter(r => r.repuesto_id !== repuestoId)
        setReemplazantes(nuevosReemplazantes)
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const valorActual = nuevosReemplazantes.map(r => 
            `${r.codigo_material_sap || r.repuesto_codigo_material_sap} - ${r.descripcion || r.repuesto_descripcion}`
          ).join(', ') || ''
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio: 'REEMPLAZANTES',
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Reemplazante eliminado correctamente')
      } else {
        toast.error('Error al eliminar el reemplazante')
      }
    } catch (error) {
      console.error('Error al eliminar reemplazante:', error)
      toast.error('Error al eliminar el reemplazante')
    }
  }

  // Función para agregar sustituto (homólogo)
  const handleAgregarSustituto = async (repuesto) => {
    if (!materialData) return
    
    try {
      const response = await putRelacionHomologo({
        revisionId: materialData.revision_id,
        homologoId: repuesto.repuesto_id,
        origen: 'AXIOMER',
      })
      
      if (response && response.success) {
        const nuevosSustitutos = [...sustitutos, {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          descripcion: repuesto.repuesto_descripcion
        }]
        setSustitutos(nuevosSustitutos)
        setRepuestosDisponibles([])
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const valorAnterior = sustitutos.map(s => 
            `${s.codigo_material_sap || s.repuesto_codigo_material_sap} - ${s.descripcion || s.repuesto_descripcion}`
          ).join(', ') || ''
          
          const valorActual = nuevosSustitutos.map(s => 
            `${s.codigo_material_sap || s.repuesto_codigo_material_sap} - ${s.descripcion || s.repuesto_descripcion}`
          ).join(', ')
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio: 'SUSTITUTOS',
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Sustituto agregado correctamente')
      } else {
        toast.error('Error al agregar el sustituto')
      }
    } catch (error) {
      console.error('Error al agregar sustituto:', error)
      toast.error('Error al agregar el sustituto')
    }
  }

  // Función para eliminar sustituto (homólogo)
  const handleEliminarSustituto = async (repuestoId) => {
    if (!materialData) return
    
    try {
      const response = await putDesvincularHomologo({
        revisionId: materialData.revision_id,
        homologoId: repuestoId,
      })
      
      if (response && response.success) {
        const valorAnterior = sustitutos.map(s => 
          `${s.codigo_material_sap || s.repuesto_codigo_material_sap} - ${s.descripcion || s.repuesto_descripcion}`
        ).join(', ') || ''
        
        const nuevosSustitutos = sustitutos.filter(s => s.repuesto_id !== repuestoId)
        setSustitutos(nuevosSustitutos)
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const valorActual = nuevosSustitutos.map(s => 
            `${s.codigo_material_sap || s.repuesto_codigo_material_sap} - ${s.descripcion || s.repuesto_descripcion}`
          ).join(', ') || ''
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio: 'SUSTITUTOS',
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Sustituto eliminado correctamente')
      } else {
        toast.error('Error al eliminar el sustituto')
      }
    } catch (error) {
      console.error('Error al eliminar sustituto:', error)
      toast.error('Error al eliminar el sustituto')
    }
  }

  // Función para agregar modelo de aplicación
  const handleAgregarModelo = async (modelo) => {
    if (!materialData) return
    
    const esPrincipal = notificacionSeleccionada?.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL'
    const tipoCambio = esPrincipal ? 'MODELOS_APLICACION_PRINCIPAL' : 'MODELOS_APLICACION'
    
    try {
      const response = await putRelacionModelo({
        revisionId: materialData.revision_id,
        modeloId: modelo.id,
        esPrincipal: esPrincipal ? 1 : 0,
      })
      
      if (response && response.success) {
        const nuevoModelo = {
          id: modelo.id,
          nombre: modelo.nombre || modelo.modelo_nombre
        }
        
        let nuevosModelos
        if (esPrincipal) {
          nuevosModelos = [...modelosAplicacionPrincipal, nuevoModelo]
          setModelosAplicacionPrincipal(nuevosModelos)
        } else {
          nuevosModelos = [...modelosAplicacion, nuevoModelo]
          setModelosAplicacion(nuevosModelos)
        }
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const listaAnterior = esPrincipal ? modelosAplicacionPrincipal : modelosAplicacion
          const valorAnterior = listaAnterior.map(m => m.nombre || m.modelo_nombre).join(', ') || ''
          const valorActual = nuevosModelos.map(m => m.nombre || m.modelo_nombre).join(', ')
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio,
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Modelo agregado correctamente')
      } else {
        toast.error('Error al agregar el modelo')
      }
    } catch (error) {
      console.error('Error al agregar modelo:', error)
      toast.error('Error al agregar el modelo')
    }
  }

  // Función para eliminar modelo de aplicación
  const handleEliminarModelo = async (modeloId) => {
    if (!materialData) return
    
    const esPrincipal = notificacionSeleccionada?.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL'
    const tipoCambio = esPrincipal ? 'MODELOS_APLICACION_PRINCIPAL' : 'MODELOS_APLICACION'
    
    try {
      const response = await putDesvincularModelo({
        revisionId: materialData.revision_id,
        modeloId,
        esPrincipal: esPrincipal ? 1 : 0,
      })
      
      if (response && response.success) {
        const listaAnterior = esPrincipal ? modelosAplicacionPrincipal : modelosAplicacion
        const valorAnterior = listaAnterior.map(m => m.nombre || m.modelo_nombre).join(', ') || ''
        
        let nuevosModelos
        if (esPrincipal) {
          nuevosModelos = modelosAplicacionPrincipal.filter(m => m.id !== modeloId)
          setModelosAplicacionPrincipal(nuevosModelos)
        } else {
          nuevosModelos = modelosAplicacion.filter(m => m.id !== modeloId)
          setModelosAplicacion(nuevosModelos)
        }
        
        // Obtener usuario actual y notificar cambio
        try {
          const userInfo = await getIam()
          const usuarioId = userInfo?.id || null
          
          const valorActual = nuevosModelos.map(m => m.nombre || m.modelo_nombre).join(', ') || ''
          
          await postNotificarCambioResponsable({
            validadorId: usuarioId,
            revisionId: materialData.revision_id,
            tipoCambio,
            valorAnterior,
            valorActual,
          })
        } catch (error) {
          console.error('Error al notificar cambio al responsable:', error)
        }
        
        toast.success('Modelo eliminado correctamente')
      } else {
        toast.error('Error al eliminar el modelo')
      }
    } catch (error) {
      console.error('Error al eliminar modelo:', error)
      toast.error('Error al eliminar el modelo')
    }
  }

  // Función para formatear jerarquía de JSON a string legible
  const formatearJerarquia = (valor) => {
    if (!valor) return ''
    
    try {
      // Intentar parsear si es un JSON string
      const jerarquia = typeof valor === 'string' ? JSON.parse(valor) : valor
      
      if (typeof jerarquia === 'object' && jerarquia !== null) {
        // Construir la cadena con separadores ">"
        const partes = [
          jerarquia.categoria,
          jerarquia.subcategoria,
          jerarquia.segmento,
          jerarquia.subsegmento,
          jerarquia.marca
        ].filter(parte => parte && parte.trim() !== '')
        
        return partes.join(' > ')
      }
    } catch (error) {
      // Si no es JSON válido, retornar el valor original
      return valor
    }
    
    return valor
  }

  // Limpiar estados al cerrar el modal
  const handleCerrarModal = () => {
    setModalVisible(false)
    setNotificacionSeleccionada(null)
    setMaterialData(null)
    setDescripcionCorrecta('')
    setReemplazantes([])
    setSustitutos([])
    setModelosAplicacion([])
    setModelosAplicacionPrincipal([])
    setRepuestosDisponibles([])
    setCodigoBusqueda('')
    setMarcaSeleccionada(null)
    setCategoriaSeleccionada(null)
    setSubcategoriaSeleccionada(null)
    setSegmentoSeleccionado(null)
    setSubsegmentoSeleccionado(null)
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'en_proceso',
        header: 'En proceso',
        size: 130,
        Cell: ({ row }) => {
          const notificacion = row.original
          const id = notificacion.id_notificacion
          const valor = notificacion.en_proceso === 1 || notificacion.en_proceso === '1'
          return (
            <Checkbox
              checked={!!valor}
              onChange={(e) => handleEnProcesoChange(id, e.target.checked)}
            />
          )
        },
      },
      {
        accessorKey: 'actualizacion_catalogo_corregido',
        header: 'Catálogo corregido',
        size: 160,
        Cell: ({ row }) => {
          const notificacion = row.original
          const id = notificacion.id_notificacion
          const revisionId = notificacion.revision_id
          const esVariableActualizacionCatalogo =
            filtroVariable === 'ACTUALIZACION_CATALOGO'
          const tieneRevision = revisionId != null && revisionId !== ''
          const valor =
            notificacion.actualizacion_catalogo_corregido === 1 ||
            notificacion.actualizacion_catalogo_corregido === '1'
          const deshabilitado =
            !esVariableActualizacionCatalogo || !tieneRevision
          const titleCatalogo = !esVariableActualizacionCatalogo
            ? 'Filtra por Variable: ACTUALIZACION CATALOGO para habilitar'
            : !tieneRevision
              ? 'Sin revision_id en la notificación'
              : undefined
          return (
            <Checkbox
              checked={!!valor}
              disabled={deshabilitado}
              title={titleCatalogo}
              onChange={(e) =>
                handleCatalogoCorregidoChange(id, revisionId, e.target.checked)
              }
            />
          )
        },
      },
      {
        accessorKey: 'id_notificacion',
        header: 'ID',
        size: 80,
      },
      {
        accessorKey: 'codigo_material_sap',
        header: 'Código Material SAP',
        size: 150,
      },
      {
        accessorKey: 'prioridad',
        header: 'Prioridad',
        size: 130,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 120,
        Cell: ({ cell }) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: cell.getValue() === 'PENDIENTE' ? '#fef3c7' : '#d1fae5',
            color: cell.getValue() === 'PENDIENTE' ? '#92400e' : '#065f46'
          }}>
            {cell.getValue() || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'fecha_deteccion',
        header: 'Fecha Detección',
        size: 180,
      },
      {
        accessorKey: 'urgente',
        header: 'Urgente',
        size: 100,
        Cell: ({ cell }) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: cell.getValue() === 1 || cell.getValue() === '1' ? '#fee2e2' : '#e5e7eb',
            color: cell.getValue() === 1 || cell.getValue() === '1' ? '#991b1b' : '#374151'
          }}>
            {cell.getValue() === 1 || cell.getValue() === '1' ? 'Sí' : 'No'}
          </span>
        ),
      },
      {
        accessorKey: 'tipo_cambio',
        header: 'Tipo Cambio',
        size: 150,
      },
      {
        accessorKey: 'vertical',
        header: 'Vertical',
        size: 150,
      },
      {
        accessorKey: 'valor_sap',
        header: 'Valor SAP',
        size: 250,
      },
      {
        accessorKey: 'valor_actual',
        header: 'Valor Actual',
        size: 250,
      },
      {
        accessorKey: 'observacion',
        header: 'Observación',
        size: 220,
        Cell: ({ row }) => {
          const raw = row.original.observacion
          const full =
            raw != null && String(raw).trim() !== '' ? String(raw).trim() : ''
          if (!full) {
            return <span style={{ color: '#9ca3af' }}>—</span>
          }
          return (
            <Tooltip
              title={
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {full}
                </span>
              }
              placement="topLeft"
              mouseEnterDelay={0.15}
              styles={{
                body: { maxWidth: 520, maxHeight: 360, overflowY: 'auto' },
              }}
            >
              <span
                style={{
                  display: 'block',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                }}
              >
                {full}
              </span>
            </Tooltip>
          )
        },
      },
      {
        id: 'acciones',
        header: 'Acciones',
        size: 120,
        Cell: ({ row }) => {
          const notificacion = row.original
          const tiposPermitidos = [
            'DESCRIPCION', 
            'REEMPLAZANTES', 
            'SUSTITUTOS',
            'JERARQUIA',
            'MARCA',
            'MODELOS_APLICACION_PRINCIPAL',
            'MODELOS_APLICACION'
          ]
          
          if (!tiposPermitidos.includes(notificacion.tipo_cambio)) {
            return null
          }
          
          return (
            <Button
              type="primary"
              size="small"
              icon={<FiEdit />}
              onClick={() => handleAbrirModal(notificacion)}
              style={{ marginRight: '8px' }}
            >
              Cambiar
            </Button>
          )
        },
      },
 
      {
        accessorKey: 'fecha_validacion',
        header: 'Fecha Validación',
        size: 150,
      },
      {
        accessorKey: 'valor_anterior_validador',
        header: 'Valor Anterior Validador',
        size: 150,
        Cell: ({ cell, row }) => {
          const valor = cell.getValue()
          const tipoCambio = row.original.tipo_cambio
          
          if (tipoCambio === 'JERARQUIA' && valor) {
            return <span>{formatearJerarquia(valor)}</span>
          }
          
          return <span>{valor || '-'}</span>
        },
      },
      {
        accessorKey: 'valor_actual_validador',
        header: 'Valor Actual Validador',
        size: 150,
        Cell: ({ cell, row }) => {
          const valor = cell.getValue()
          const tipoCambio = row.original.tipo_cambio
          
          if (tipoCambio === 'JERARQUIA' && valor) {
            return <span>{formatearJerarquia(valor)}</span>
          }
          
          return <span>{valor || '-'}</span>
        },
      },
      {
        accessorKey: 'nombre_validador',
        header: 'Nombre Validador',
        size: 150,
      },
    ],
    [handleEnProcesoChange, handleCatalogoCorregidoChange, filtroVariable]
  )

  // Estadísticas
  const estadisticas = useMemo(() => {
    const total = notificaciones.length
    const pendientes = notificaciones.filter(n => n.estado === 'PENDIENTE').length
    const urgentes = notificaciones.filter(n => n.urgente === 1 || n.urgente === '1').length
    return { total, pendientes, urgentes }
  }, [notificaciones])

  // Función para descargar Excel con los datos filtrados
  const descargarExcel = () => {
    try {
      if (notificaciones.length === 0) {
        toast.warning('No hay datos para exportar')
        return
      }

      // Preparar los datos para Excel
      const datosParaExcel = notificaciones.map((notificacion) => ({
        'En proceso': notificacion.en_proceso === 1 || notificacion.en_proceso === '1' ? 'Sí' : 'No',
        'Catálogo corregido':
          notificacion.actualizacion_catalogo_corregido === 1 ||
          notificacion.actualizacion_catalogo_corregido === '1'
            ? 'Sí'
            : 'No',
        'ID': notificacion.id_notificacion || '',
        'Código Material SAP': notificacion.codigo_material_sap || '',
        'Prioridad': notificacion.prioridad || '',
        'Estado': notificacion.estado || '',
        'Fecha Detección': notificacion.fecha_deteccion || '',
        'Urgente': notificacion.urgente === 1 || notificacion.urgente === '1' ? 'Sí' : 'No',
        'Tipo Cambio': notificacion.tipo_cambio || '',
        'Vertical': notificacion.vertical || '',
        'Valor SAP': notificacion.valor_sap || '',
        'Valor Actual': notificacion.valor_actual || '',
        'Observación': notificacion.observacion || '',
      }))

      // Crear el archivo Excel
      const worksheet = XLSX.utils.json_to_sheet(datosParaExcel)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Notificaciones')

      // Generar nombre de archivo con fecha
      const fechaActual = new Date().toISOString().split('T')[0]
      const nombreArchivo = `Validadores_Notificaciones_${fechaActual}.xlsx`

      // Descargar el archivo
      XLSX.writeFile(workbook, nombreArchivo)

      toast.success(`Excel descargado correctamente (${notificaciones.length} registros)`)
    } catch (error) {
      console.error('Error al generar el Excel:', error)
      toast.error('Error al descargar el Excel')
    }
  }

  return (
    <div className="validadores-container">
      {/* Header */}
      <div className="validadores-header">
        <div className="validadores-header-content">
          <div className="validadores-title-section">
            <FiList className="validadores-title-icon" />
            <div>
              <h1 className="validadores-title">Validadores</h1>
              <p className="validadores-subtitle">
                Gestiona y visualiza las notificaciones de cambios ({estadisticas.total} notificaciones)
              </p>
            </div>
          </div>
          {estadisticas.urgentes > 0 && (
            <div className="validadores-alert">
              <FiAlertCircle />
              <span>{estadisticas.urgentes} urgentes</span>
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="validadores-filtros">
        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Código Material:</span>
          <input
            type="text"
            className="validadores-filtro-input"
            placeholder="Buscar por código..."
            value={filtroCodigoMaterial}
            onChange={(e) => setFiltroCodigoMaterial(e.target.value)}
          />
        </div>

        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Estado:</span>
          <select
            className="validadores-filtro-select"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="REALIZADO">REALIZADO</option>
            <option value="EN_PROCESO">EN PROCESO</option>
            <option value="SIN_CAMBIO">SIN CAMBIO</option>
          </select>
        </div>

        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Variable:</span>
          <select
            className="validadores-filtro-select"
            value={filtroVariable}
            onChange={(e) => setFiltroVariable(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="JERARQUIA">JERARQUIA</option>
            <option value="MARCA">MARCA</option>
            <option value="SUSTITUTOS">SUSTITUTOS</option>
            <option value="REEMPLAZANTES">REEMPLAZANTES</option>
            <option value="MODELOS_APLICACION_PRINCIPAL">MODELOS_APLICACION_PRINCIPAL</option>
            <option value="MODELOS_APLICACION">MODELOS_APLICACION</option>
            <option value="DESCRIPCION">DESCRIPCION</option>
            <option value="ACTUALIZACION_CATALOGO">ACTUALIZACION CATALOGO</option>
          </select>
        </div>

        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Urgente:</span>
          <select
            className="validadores-filtro-select"
            value={filtroUrgente}
            onChange={(e) => setFiltroUrgente(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="1">Sí</option>
            <option value="0">No</option>
          </select>
        </div>

        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Vertical:</span>
          <select
            className="validadores-filtro-select"
            value={filtroVertical}
            onChange={(e) => setFiltroVertical(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="Repuestos AUTECO SAS">Repuestos AUTECO SAS</option>
            <option value="Repuestos VICTORY+">Repuestos VICTORY+</option>
            <option value="Repuestos ESPECIALES">Repuestos ESPECIALES</option>
            <option value="Repuestos AUTECO ELECTRIC">Repuestos AUTECO ELECTRIC</option>
          </select>
        </div>

        <div className="validadores-filtros-grupo">
          <span className="validadores-filtros-label">Prioridad:</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <Checkbox
                checked={prioridadTodas}
                onChange={(e) => {
                  const {checked} = e.target
                  setPrioridadTodas(checked)
                  if (checked) setFiltroPrioridad([])
                }}
              />
              Todas
            </label>
            {!prioridadTodas &&
              prioridades.map((p) => {
                const valor = p.prioridad ?? p
                return (
                  <label
                    key={valor}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Checkbox
                      checked={filtroPrioridad.includes(valor)}
                      onChange={(e) => {
                        const {checked} = e.target
                        setFiltroPrioridad((prev) =>
                          checked ? [...prev, valor] : prev.filter((v) => v !== valor)
                        )
                      }}
                    />
                    {valor}
                  </label>
                )
              })}
          </div>
        </div>

        <button
          type="button"
          className="validadores-search-btn"
          onClick={cargarNotificaciones}
          disabled={loading}
        >
          <FiSearch />
          Buscar
        </button>

        <button
          type="button"
          className="validadores-search-btn"
          onClick={limpiarFiltros}
          disabled={loading}
          style={{ marginLeft: '8px' }}
        >
          <FiX />
          Limpiar filtros
        </button>

        <button
          type="button"
          className="validadores-search-btn"
          onClick={descargarExcel}
          disabled={loading || notificaciones.length === 0}
          style={{ marginLeft: '8px' }}
        >
          <FiDownload />
          Descargar Excel
        </button>
      </div>

      {/* Tabla */}
      <div className="validadores-content">
        <div className="validadores-table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={notificaciones}
            enableColumnResizing
            enableColumnOrdering
            enablePinning
            enableStickyHeader
            enableDensityToggle
            enableFullScreenToggle
            enableGlobalFilter
            initialState={{
              density: 'compact',
              showGlobalFilter: true,
            }}
            state={{
              isLoading: loading,
            }}
            muiTableContainerProps={{
              sx: { maxHeight: 'calc(100vh - 320px)' }
            }}
            muiTablePaperProps={{
              sx: {
                boxShadow: 'none',
                border: '1px solid #e5e7eb',
              }
            }}
          />
        </div>
      </div>

      {/* Modal de edición */}
      <Modal
        title={
          notificacionSeleccionada
            ? `Editar ${notificacionSeleccionada.tipo_cambio} - Material ${notificacionSeleccionada.codigo_material_sap}`
            : 'Editar Material'
        }
        open={modalVisible}
        onCancel={handleCerrarModal}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCerrarModal}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleGuardarCambios}
            loading={loadingMaterial}
          >
            Guardar
          </Button>,
        ]}
      >
        {loadingMaterial ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Cargando información del material...
          </div>
        ) : (
          <div style={{ padding: '20px 0' }}>
            {notificacionSeleccionada?.tipo_cambio === 'DESCRIPCION' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Descripción Correcta
                  </label>
                  <Input.TextArea
                    rows={4}
                    value={descripcionCorrecta}
                    onChange={(e) => setDescripcionCorrecta(e.target.value.toUpperCase())}
                    placeholder="Ingrese la descripción correcta"
                    style={{ width: '100%' }}
                  />
                  {materialData && (
                    <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
                      Descripción actual en SAP: {materialData.repuesto_descripcion}
                    </div>
                  )}
                </div>
              </div>
            )}

            {notificacionSeleccionada?.tipo_cambio === 'REEMPLAZANTES' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Reemplazantes
                  </label>
                  
                  {/* Buscar repuesto para agregar */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <Input
                      placeholder="Buscar por código SAP..."
                      value={codigoBusqueda}
                      onChange={(e) => setCodigoBusqueda(e.target.value)}
                      onPressEnter={handleBuscarRepuesto}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      onClick={handleBuscarRepuesto}
                      loading={buscandoRepuesto}
                      icon={<PlusOutlined />}
                    >
                      Buscar
                    </Button>
                  </div>

                  {/* Lista de repuestos encontrados */}
                  {repuestosDisponibles.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Repuesto encontrado:</div>
                      {repuestosDisponibles.map((repuesto) => (
                        <div
                          key={repuesto.repuesto_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          <div>
                            <strong>{repuesto.repuesto_codigo_material_sap}</strong> - {repuesto.repuesto_descripcion}
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleAgregarReemplazante(repuesto)}
                          >
                            Agregar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista de reemplazantes actuales */}
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Reemplazantes actuales:</div>
                    {reemplazantes.length === 0 ? (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>No hay reemplazantes agregados</div>
                    ) : (
                      <Space wrap>
                        {reemplazantes.map((reemplazante) => (
                          <Tag
                            key={reemplazante.repuesto_id}
                            closable
                            onClose={() => handleEliminarReemplazante(reemplazante.repuesto_id)}
                            style={{ marginBottom: '8px' }}
                          >
                            {reemplazante.codigo_material_sap || reemplazante.repuesto_codigo_material_sap} - {reemplazante.descripcion || reemplazante.repuesto_descripcion}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                </div>
              </div>
            )}

            {notificacionSeleccionada?.tipo_cambio === 'SUSTITUTOS' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Sustitutos
                  </label>
                  
                  {/* Buscar repuesto para agregar */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <Input
                      placeholder="Buscar por código SAP..."
                      value={codigoBusqueda}
                      onChange={(e) => setCodigoBusqueda(e.target.value)}
                      onPressEnter={handleBuscarRepuesto}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      onClick={handleBuscarRepuesto}
                      loading={buscandoRepuesto}
                      icon={<PlusOutlined />}
                    >
                      Buscar
                    </Button>
                  </div>

                  {/* Lista de repuestos encontrados */}
                  {repuestosDisponibles.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Repuesto encontrado:</div>
                      {repuestosDisponibles.map((repuesto) => (
                        <div
                          key={repuesto.repuesto_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          <div>
                            <strong>{repuesto.repuesto_codigo_material_sap}</strong> - {repuesto.repuesto_descripcion}
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleAgregarSustituto(repuesto)}
                          >
                            Agregar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista de sustitutos actuales */}
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Sustitutos actuales:</div>
                    {sustitutos.length === 0 ? (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>No hay sustitutos agregados</div>
                    ) : (
                      <Space wrap>
                        {sustitutos.map((sustituto) => (
                          <Tag
                            key={sustituto.repuesto_id}
                            closable
                            onClose={() => handleEliminarSustituto(sustituto.repuesto_id)}
                            style={{ marginBottom: '8px' }}
                          >
                            {sustituto.codigo_material_sap || sustituto.repuesto_codigo_material_sap} - {sustituto.descripcion || sustituto.repuesto_descripcion}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                </div>
              </div>
            )}

            {notificacionSeleccionada?.tipo_cambio === 'MARCA' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Marca
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Seleccione una marca"
                    value={marcaSeleccionada}
                    onChange={setMarcaSeleccionada}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={marcas.map(marca => ({
                      value: marca.id,
                      label: marca.nombre
                    }))}
                  />
                  {materialData && (
                    <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
                      Marca actual: {materialData.marca_nombre || 'No definida'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {notificacionSeleccionada?.tipo_cambio === 'JERARQUIA' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Jerarquía
                  </label>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Categoría:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Seleccione una categoría"
                      value={categoriaSeleccionada}
                      onChange={(value) => {
                        setCategoriaSeleccionada(value)
                        if (cascadaActiva) {
                          // La cascada siempre se respeta: cambiar el padre limpia los hijos
                          setSubcategoriaSeleccionada(null)
                          setSegmentoSeleccionado(null)
                          setSubsegmentoSeleccionado(null)
                          cambiarCategoriaCascada(value)
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(cascadaActiva ? categoriasCascada : categorias).map(cat => ({
                        value: cat.id,
                        label: cat.nombre
                      }))}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Subcategoría:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder={cascadaActiva && !categoriaSeleccionada ? 'Seleccione una categoría primero' : 'Seleccione una subcategoría'}
                      disabled={cascadaActiva && !categoriaSeleccionada}
                      value={subcategoriaSeleccionada}
                      onChange={(value) => {
                        setSubcategoriaSeleccionada(value)
                        if (cascadaActiva) {
                          setSegmentoSeleccionado(null)
                          setSubsegmentoSeleccionado(null)
                          cambiarSubcategoriaCascada(value)
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(cascadaActiva ? subcategoriasCascada : subcategorias).map(sub => ({
                        value: sub.id,
                        label: sub.nombre
                      }))}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Segmento:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder={cascadaActiva && !subcategoriaSeleccionada ? 'Seleccione una subcategoría primero' : 'Seleccione un segmento'}
                      disabled={cascadaActiva && !subcategoriaSeleccionada}
                      value={segmentoSeleccionado}
                      onChange={(value) => {
                        setSegmentoSeleccionado(value)
                        if (cascadaActiva) {
                          setSubsegmentoSeleccionado(null)
                          cambiarSegmentoCascada(value)
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(cascadaActiva ? segmentosCascada : segmentos).map(seg => ({
                        value: seg.id,
                        label: seg.nombre
                      }))}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Subsegmento:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder={cascadaActiva && !segmentoSeleccionado ? 'Seleccione un segmento primero' : 'Seleccione un subsegmento'}
                      disabled={cascadaActiva && !segmentoSeleccionado}
                      value={subsegmentoSeleccionado}
                      onChange={setSubsegmentoSeleccionado}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(cascadaActiva ? subsegmentosCascada : subsegmentos).map(sub => ({
                        value: sub.id,
                        label: sub.nombre
                      }))}
                    />
                  </div>

                  {/* <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Marca:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Seleccione una marca"
                      value={marcaSeleccionada}
                      onChange={setMarcaSeleccionada}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={marcas.map(marca => ({
                        value: marca.id,
                        label: marca.nombre
                      }))}
                    />
                  </div> */}
                </div>
              </div>
            )}

            {(notificacionSeleccionada?.tipo_cambio === 'MODELOS_APLICACION' || notificacionSeleccionada?.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL') && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    {notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL' ? 'Modelos de Aplicación Principal' : 'Modelos de Aplicación'}
                    {modelosDisponibles.length > 0 && (
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
                        ({modelosDisponibles.length} disponibles)
                      </span>
                    )}
                  </label>
                  
                  {/* Buscar modelo para agregar */}
                  <div style={{ marginBottom: '16px' }}>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Buscar y seleccionar un modelo..."
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      notFoundContent={
                        modelosDisponibles.length === 0
                          ? 'No hay modelos cargados'
                          : 'Sin coincidencias'
                      }
                      filterOption={(input, option) =>
                        String(option?.label ?? '')
                          .toLowerCase()
                          .includes(String(input ?? '').toLowerCase())
                      }
                      onSelect={(value) => {
                        const modelo = modelosDisponibles.find(
                          (m) => String(m.id) === String(value),
                        )
                        if (modelo) {
                          handleAgregarModelo(modelo)
                        }
                      }}
                      options={(modelosDisponibles || [])
                        .filter((modelo) => modelo != null && modelo.id != null)
                        .map((modelo) => ({
                          value: modelo.id,
                          label:
                            modelo.nombre ||
                            modelo.modelo_nombre ||
                            `ID ${modelo.id}`,
                        }))}
                    />
                  </div>

                  {/* Lista de modelos actuales */}
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL' ? 'Modelos principales actuales:' : 'Modelos actuales:'}
                    </div>
                    {((notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL' ? modelosAplicacionPrincipal : modelosAplicacion).length === 0) ? (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>No hay modelos agregados</div>
                    ) : (
                      <Space wrap>
                        {(notificacionSeleccionada.tipo_cambio === 'MODELOS_APLICACION_PRINCIPAL' ? modelosAplicacionPrincipal : modelosAplicacion).map((modelo) => (
                          <Tag
                            key={modelo.id}
                            closable
                            onClose={() => handleEliminarModelo(modelo.id)}
                            style={{ marginBottom: '8px' }}
                          >
                            {modelo.nombre || modelo.modelo_nombre}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AxiomaValidadoresContainer
